import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams, useSearch, Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { formatUserName, invalidateTaskQueries } from "@/lib/task-query-utils";
import { apiRequest } from "@/lib/queryClient";
import { ActivityFeed, type ActivityRecord } from "@/components/tasks/activity-feed";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Save,
  Pencil,
  ArrowLeft,
  Archive,
  Calendar as CalendarIcon,
  Link2,
  ClipboardList,
  History,
  FileText,
  Share,
  StickyNote,
  LayoutList,
  Copy,
  FileDown,
  AlertTriangle,
  MoreVertical,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { getDisplayName } from "@/lib/task-query-utils";
import { queryClient } from "@/lib/queryClient";
import type { ExportSettings } from "@/lib/export-settings";
import { DEFAULT_EXPORT_SETTINGS } from "@/lib/export-settings";
import { formatMeetingMarkdown, generateMeetingPdf, getMeetingIncompleteFields } from "@/lib/meeting-export";
import { useTaskDetailData } from "@/components/tasks/use-task-detail-data";
import { CommentSection } from "@/components/tasks/comment-section";
import { TaskChecklistSection } from "@/components/tasks/task-checklist-section";
import { TaskAnimalLinker } from "@/components/tasks/task-animal-linker";
import { TaskInteractionLinker } from "@/components/tasks/task-interaction-linker";
import { TaskLinkSection } from "@/components/tasks/task-link-section";
import { TaskAttachmentsSection } from "@/components/tasks/task-attachments-section";
import { TaskLabelSection } from "@/components/tasks/task-label-section";
import { TaskAssigneeSelect } from "@/components/tasks/task-assignee-select";
import { TaskStatusSelect } from "@/components/tasks/task-status-select";
import { TaskDueDatePicker } from "@/components/tasks/task-due-date-picker";
import { EntityNotesSection } from "@/components/notes/entity-notes-section";
import { PriorityBadge, PRIORITY_OPTIONS } from "@/components/tasks/priority-badge";
import { usePageTitle } from "@/hooks/use-page-title";
import { boardTypeFromContext, boardTypeFromRoute, getBoardTypeConfig, getBoardRoutePath, getBoardLabel, getBoardEntityLabel } from "@/lib/board-types";

export default function TaskDetailPage() {
  usePageTitle("Task Detail");
  const [location, setLocation] = useLocation();
  const params = useParams<{ taskId: string }>();
  const taskId = params.taskId;
  const searchString = useSearch();
  const fromContext = new URLSearchParams(searchString).get("from");
  const routeBoardType = fromContext ? boardTypeFromContext(fromContext) : boardTypeFromRoute(location);
  const [activeTab, setActiveTab] = useState("details");
  const [highlightedSection, setHighlightedSection] = useState<string | null>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scrollToSection = useCallback((sectionId: string) => {
    setActiveTab("details");
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedSection(sectionId);
      highlightTimeoutRef.current = setTimeout(() => setHighlightedSection(null), 2000);
    }, 100);
  }, []);

  const {
    task,
    isLoading,
    boardId,
    members,
    activeStatuses,
    availableLabels,
    isOverdue,
    canEdit,
    canComment,
    userRole,
    currentUserId,
    isEditing,
    setIsEditing,
    editTitle,
    setEditTitle,
    editDescription,
    setEditDescription,
    editStatusId,
    setEditStatusId,
    editAssignee,
    setEditAssignee,
    editDueDate,
    setEditDueDate,
    editPriority,
    setEditPriority,
    datePickerOpen,
    setDatePickerOpen,
    newAnimalSearch,
    setNewAnimalSearch,
    newInteractionSearch,
    setNewInteractionSearch,
    animalSearchResults,
    interactionSearchResults,
    updateMutation,
    archiveMutation,
    isArchived,
    handleSave,
  } = useTaskDetailData({
    taskId,
    onArchiveSuccess: () => {
      const routePath = getBoardRoutePath(routeBoardType);
      setLocation(boardId ? `${routePath}?boardId=${boardId}` : routePath);
    },
  });

  const { data: boardData } = useQuery<{ boards: Array<{ id: string; name: string }> }>({
    queryKey: ["/api/task-boards", "memberOnly"],
    queryFn: async () => {
      const res = await fetch("/api/task-boards?memberOnly=true", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch boards");
      return res.json();
    },
  });

  const { data: activityData, isLoading: activityLoading } = useQuery<{ activity: ActivityRecord[] }>({
    queryKey: ["/api/tasks", taskId, "activity"],
    enabled: !!taskId,
  });

  const { data: notesData } = useQuery<{ note: { content: string } | null }>({
    queryKey: ["/api/notes", "task", taskId],
    enabled: !!taskId,
  });

  const boardType = task?.type || routeBoardType;
  const config = getBoardTypeConfig(boardType);
  const isMeetingContext = boardType === "meeting";

  const boardName = boardData?.boards?.find(b => b.id === boardId)?.name ?? "Board";
  const { toast } = useToast();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

  const duplicateMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", `/api/tasks/${taskId}/duplicate`);
    },
    onSuccess: (data: { task: { id: string; type?: string } }) => {
      invalidateTaskQueries();
      toast({ title: `${config.labels.entity} duplicated`, description: "A copy has been created." });
      const newId = data.task.id;
      const targetRoute = getBoardRoutePath(data.task.type || boardType);
      setLocation(`${targetRoute}/${newId}`);
    },
    onError: () => {
      toast({ variant: "destructive", title: "Duplicate failed", description: "Could not duplicate this task." });
    },
  });

  const exportSettings = (task?.exportSettings as ExportSettings | null) ?? DEFAULT_EXPORT_SETTINGS;
  const formats = exportSettings.formats;

  function getNotesContent(): string | null {
    const cached = queryClient.getQueryData<{ note: { content: string } | null }>(["/api/notes", "task", taskId]);
    return cached?.note?.content ?? null;
  }

  function buildExportData() {
    if (!task) return null;
    return { task, notes: getNotesContent(), isMeeting: isMeetingContext, boardType };
  }

  const incompleteFields = task ? getMeetingIncompleteFields(
    { task, notes: getNotesContent(), isMeeting: isMeetingContext, boardType },
    exportSettings,
  ) : [];

  async function copyToClipboard(text: string): Promise<boolean> {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand("copy");
      document.body.removeChild(textarea);
      return success;
    } catch {
      return false;
    }
  }

  const handleCopyLink = async () => {
    if (!task) return;
    const dateStr = task.dueDate
      ? new Date(task.dueDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : "No date set";
    const url = `${window.location.origin}${config.routePath}/${taskId}`;
    const summary = `${task.title} - ${dateStr} - ${url}`;
    const ok = await copyToClipboard(summary);
    setShareDialogOpen(false);
    toast(ok
      ? { title: "Link copied", description: "Title, date, and link copied to clipboard." }
      : { variant: "destructive", title: "Copy failed", description: "Unable to copy to clipboard." }
    );
  };

  const handleCopyMarkdown = async () => {
    const data = buildExportData();
    if (!data) return;
    const md = formatMeetingMarkdown(data, exportSettings);
    const ok = await copyToClipboard(md);
    setShareDialogOpen(false);
    toast(ok
      ? { title: "Markdown copied", description: `${config.labels.entity} copied as Markdown.` }
      : { variant: "destructive", title: "Copy failed", description: "Unable to copy to clipboard." }
    );
  };

  const handleDownloadPdf = () => {
    const data = buildExportData();
    if (!data) return;
    generateMeetingPdf(data, exportSettings);
    setShareDialogOpen(false);
    toast({ title: "PDF downloaded", description: `${config.labels.entity} saved as PDF.` });
  };

  useEffect(() => {
    if (!isLoading && task && window.location.hash) {
      const id = window.location.hash.slice(1);
      if (id === "notes-section") {
        setActiveTab("notes");
      } else {
        const el = document.getElementById(id);
        if (el) {
          setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
        }
      }
    }
  }, [isLoading, task]);

  if (isLoading) {
    return (
      <div className="flex-1 px-4 sm:px-6 pb-6 pt-4 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!task) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-2">
          <ClipboardList className="w-12 h-12 text-muted-foreground mx-auto" />
          <p className="text-lg font-medium">{config.labels.notFound}</p>
          <Button variant="outline" onClick={() => setLocation(config.routePath)} data-testid="button-back-to-tasks">
            <ArrowLeft className="w-4 h-4 mr-1" /> {config.labels.backToList}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 px-4 sm:px-6 pb-6 pt-4" data-testid="page-task-detail">
      <PageHeader
        breadcrumb={[
          { label: "Planning" },
          { label: config.labels.plural, href: config.routePath },
          { label: boardName, href: `${config.routePath}?boardId=${boardId}` },
          { label: task.title },
        ]}
        title={
          isEditing && canEdit ? (
            <div className="flex items-center gap-2 flex-wrap">
              <ClipboardList className="w-6 h-6 text-primary shrink-0" />
              <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} className="text-xl font-semibold flex-1 min-w-[300px] max-w-[600px]" maxLength={500} data-testid="task-input-edit-title" />
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending} data-testid="task-button-save-task">
                  <Save className="w-3 h-3 mr-1" /> Save
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} data-testid="task-button-cancel-edit">Cancel</Button>
              </div>
            </div>
          ) : (
            <span className="flex items-baseline gap-2 flex-wrap">
              <ClipboardList className="w-6 h-6 text-primary shrink-0 self-center" />
              <span data-testid="task-text-title">
                {config.labels.titlePrefix}{task.title}
              </span>
              {task.dueDate && (
                <span className="text-base font-normal text-muted-foreground leading-none" data-testid="task-text-meeting-date-header">
                  {new Date(task.dueDate + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                </span>
              )}
              {canEdit && (
                <Button variant="ghost" size="sm" className="shrink-0" onClick={() => setIsEditing(true)} data-testid="task-button-edit-task">
                  <Pencil className="w-3.5 h-3.5 mr-1" />
                  Edit
                </Button>
              )}
              {canEdit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="shrink-0" data-testid="task-button-actions-dropdown">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {!isArchived && (
                      <DropdownMenuItem
                        onClick={() => duplicateMutation.mutate()}
                        disabled={duplicateMutation.isPending}
                        data-testid="task-button-duplicate-detail"
                      >
                        <Copy className="w-3.5 h-3.5 mr-2" />
                        {config.labels.duplicateAction}
                      </DropdownMenuItem>
                    )}
                    {!isArchived && <DropdownMenuSeparator />}
                    {!isArchived ? (
                      <DropdownMenuItem
                        onClick={() => setArchiveDialogOpen(true)}
                        className="text-destructive"
                        data-testid="task-button-archive-task"
                      >
                        <Archive className="w-3.5 h-3.5 mr-2" />
                        Archive {config.labels.entity}
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        onClick={() => archiveMutation.mutate()}
                        disabled={archiveMutation.isPending}
                        data-testid="task-button-unarchive-task"
                      >
                        <Archive className="w-3.5 h-3.5 mr-2" />
                        {config.labels.unarchiveAction}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </span>
          )
        }
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {isMeetingContext && (
              <Button variant="outline" onClick={() => setShareDialogOpen(true)} data-testid="button-share-meeting">
                <Share className="w-4 h-4 mr-1" /> Share
              </Button>
            )}
            <Button variant="outline" onClick={() => setLocation(`${config.routePath}?boardId=${boardId}`)} data-testid="button-back-to-board">
              <ArrowLeft className="w-4 h-4 mr-1" /> {config.labels.backToBoard}
            </Button>
          </div>
        }
      />

      <Card>
        <CardContent className="pt-4 pb-4 space-y-2">
          {task.isTemplate && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted border border-border" data-testid="task-banner-template">
              <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-sm font-medium">Template</span>
              <span className="text-xs text-muted-foreground">This is a reusable template, not an active task.</span>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {task.type && task.type !== "regular" && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">Type:</span>
                <Badge variant="outline" className="capitalize" data-testid="task-badge-type">{task.type}</Badge>
              </div>
            )}
            <TaskStatusSelect
              taskId={task.id}
              currentStatusId={task.status?.id ?? ""}
              currentStatusName={task.status?.name ?? ""}
              statuses={activeStatuses}
              canEdit={canEdit}
            />
            <TaskAssigneeSelect
              taskId={task.id}
              assignee={task.assignee}
              members={members}
              canEdit={canEdit}
              boardType={boardType}
            />
            <TaskLabelSection
              taskId={task.id}
              assignedLabels={task.labels}
              availableLabels={availableLabels}
              canEdit={canEdit}
              variant="inline"
            />
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
        <TabsList data-testid="tabs-task-detail">
          <TabsTrigger value="details" data-testid="tab-details">
            <LayoutList className="w-3.5 h-3.5 mr-1.5" />
            Details
          </TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">
            <StickyNote className="w-3.5 h-3.5 mr-1.5" />
            Notes
          </TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">
            <History className="w-3.5 h-3.5 mr-1.5" />
            Activity
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardContent className="pt-4 pb-4 space-y-3">
                  <div className="flex items-center flex-wrap gap-2">
                    <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Overview</Label>
                    {canEdit && !isEditing && (
                      <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)} data-testid="task-button-edit-details">
                        <Pencil className="w-3.5 h-3.5 mr-1" />
                        Edit
                      </Button>
                    )}
                    {isEditing && canEdit && (
                      <div className="flex items-center gap-1">
                        <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending} data-testid="task-button-save-details">
                          <Save className="w-3 h-3 mr-1" /> Save
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} data-testid="task-button-cancel-details">Cancel</Button>
                      </div>
                    )}
                  </div>

                  <TaskDueDatePicker
                    date={editDueDate}
                    onChange={setEditDueDate}
                    open={datePickerOpen}
                    onOpenChange={setDatePickerOpen}
                    canEdit={canEdit}
                    isEditing={isEditing}
                    isOverdue={isOverdue}
                    dueDate={task.dueDate}
                    boardType={boardType}
                  />

                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{config.labels.description}</Label>
                    {isEditing && canEdit ? (
                      <Textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} placeholder="Add a description..." className="resize-none" maxLength={5000} data-testid="task-textarea-edit-description" />
                    ) : (
                      <p className="text-sm whitespace-pre-wrap" data-testid="task-text-description">
                        {task.description || <span className="text-muted-foreground italic">No description</span>}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card id="section-animals" className={`transition-all duration-300 ${highlightedSection === "section-animals" ? "ring-2 ring-primary ring-inset bg-primary/5 animate-pulse" : ""}`}>
                  <CardContent className="pt-6 space-y-3">
                    <TaskAnimalLinker
                      taskId={task.id}
                      animals={task.animals}
                      canEdit={canEdit}
                      animalSearchQuery={newAnimalSearch}
                      setAnimalSearchQuery={setNewAnimalSearch}
                      animalSearchResults={animalSearchResults}
                    />
                  </CardContent>
                </Card>

                <Card id="section-interactions" className={`transition-all duration-300 ${highlightedSection === "section-interactions" ? "ring-2 ring-primary ring-inset bg-primary/5 animate-pulse" : ""}`}>
                  <CardContent className="pt-6 space-y-3">
                    <TaskInteractionLinker
                      taskId={task.id}
                      interactions={task.interactions}
                      canEdit={canEdit}
                      interactionSearchQuery={newInteractionSearch}
                      setInteractionSearchQuery={setNewInteractionSearch}
                      interactionSearchResults={interactionSearchResults}
                    />
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardContent className="pt-6 space-y-4">
                  <CommentSection
                    entityType="task"
                    entityId={task.id}
                    canComment={canComment}
                    currentUserId={currentUserId}
                    mentionableUsers={members}
                  />
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card>
                <CardContent className="pt-6 space-y-3">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> Details</Label>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between gap-2">
                      <span className="text-muted-foreground">Created</span>
                      <span className="text-right text-xs text-muted-foreground/80" data-testid="task-text-created-info">
                        {new Date(task.createdAt).toLocaleDateString()}
                        {task.creator && ` by ${formatUserName(task.creator)}`}
                        {task.source === "template"
                          ? ` from template${task.sourceTemplateName ? `: ${task.sourceTemplateName}` : ""}`
                          : ""}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-muted-foreground">Updated</span>
                      <span className="text-right text-xs text-muted-foreground/80" data-testid="task-text-updated-info">
                        {new Date(task.updatedAt).toLocaleDateString()}
                        {task.updater && ` by ${formatUserName(task.updater)}`}
                      </span>
                    </div>
                    {task.source && task.source !== "template" && (
                      <div className="flex justify-between gap-2" data-testid="task-provenance">
                        <span className="text-muted-foreground">Source</span>
                        <span className="text-right">
                          {task.source === "animal" && task.animals.length > 0 ? (
                            <Link href={`/animals/${task.animals[0].animalId}`} className="underline hover-elevate" data-testid="task-provenance-link">{task.animals[0].animalName ?? "Animal"}</Link>
                          ) : task.source === "interaction" && task.interactions.length > 0 ? (
                            <>{task.interactions[0].eventType ?? "Interaction"}</>
                          ) : (
                            <span className="capitalize">{task.source}</span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6 space-y-3">
                  <TaskChecklistSection
                    taskId={task.id}
                    checklistItems={task.checklistItems}
                    canEdit={canEdit}
                    boardType={boardType}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6 space-y-3">
                  <TaskLinkSection
                    taskId={task.id}
                    links={task.links ?? []}
                    canEdit={canEdit}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6 space-y-3">
                  <TaskAttachmentsSection
                    taskId={task.id}
                    canEdit={canEdit}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="notes">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3">
              <Card>
                <CardContent className="pt-6">
                  <EntityNotesSection
                    entityType="task"
                    entityId={task.id}
                    canEdit={canEdit}
                    currentUserId={currentUserId}
                    shareLabel={task.title}
                  />
                </CardContent>
              </Card>
            </div>

            <div className="space-y-4" data-testid="notes-context-sidebar">
              <Card>
                <CardContent className="pt-4 space-y-3">
                  <TaskChecklistSection
                    taskId={task.id}
                    checklistItems={task.checklistItems}
                    canEdit={canEdit}
                    boardType={boardType}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-4 space-y-3">
                  <TaskAnimalLinker
                    taskId={task.id}
                    animals={task.animals}
                    canEdit={false}
                    animalSearchQuery=""
                    setAnimalSearchQuery={() => {}}
                    animalSearchResults={undefined}
                  />
                  {canEdit && (
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-foreground underline"
                      onClick={() => scrollToSection("section-animals")}
                      data-testid="link-animals-add-remove"
                    >
                      Add / Remove
                    </button>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-4 space-y-3">
                  <TaskInteractionLinker
                    taskId={task.id}
                    interactions={task.interactions}
                    canEdit={false}
                    interactionSearchQuery=""
                    setInteractionSearchQuery={() => {}}
                    interactionSearchResults={undefined}
                  />
                  {canEdit && (
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-foreground underline"
                      onClick={() => scrollToSection("section-interactions")}
                      data-testid="link-interactions-add-remove"
                    >
                      Add / Remove
                    </button>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-4 space-y-3">
                  <TaskLinkSection
                    taskId={task.id}
                    links={task.links ?? []}
                    canEdit={false}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-4 space-y-3">
                  <TaskAttachmentsSection
                    taskId={task.id}
                    canEdit={false}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <Card data-testid="task-activity-section">
            <CardContent className="pt-6 space-y-3">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                <History className="w-3 h-3" /> Activity
              </Label>
              <ActivityFeed
                activity={activityData?.activity ?? []}
                isLoading={activityLoading}
                showTaskTitle={false}
                maxItems={5}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent data-testid="dialog-share-meeting">
          <DialogHeader>
            <DialogTitle>Share {config.labels.entity}</DialogTitle>
          </DialogHeader>

          {incompleteFields.length > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-800 dark:text-amber-300" data-testid="text-share-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Incomplete {config.labels.entityLower}: {incompleteFields.join(", ")}</span>
            </div>
          )}

          <div className="flex flex-col gap-2">
            {formats.copyLink && (
              <div>
                <Button variant="outline" className="justify-start gap-2 w-full" onClick={handleCopyLink} data-testid="button-copy-link">
                  <Link2 className="w-4 h-4" />
                  Copy Link
                </Button>
                <p className="text-xs text-muted-foreground mt-1 ml-1" data-testid="text-copy-link-note">
                  Only BETI users with access to this {config.labels.entityLower.split(" ")[0]} board can view this {config.labels.entityLower}.
                </p>
              </div>
            )}
            {formats.copyMarkdown && (
              <Button variant="outline" className="justify-start gap-2" onClick={handleCopyMarkdown} data-testid="button-copy-markdown">
                <Copy className="w-4 h-4" />
                Copy as Markdown
              </Button>
            )}
            {formats.downloadPdf && (
              <Button variant="outline" className="justify-start gap-2" onClick={handleDownloadPdf} data-testid="button-download-pdf">
                <FileDown className="w-4 h-4" />
                Download PDF
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{config.labels.archiveTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {config.labels.archiveDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-archive">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { archiveMutation.mutate(); setArchiveDialogOpen(false); }} data-testid="button-confirm-archive">
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
